/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {getSetting, ResponseData, showDialog, validateEmail} from '@dvrd/dvr-controls';
import {IdealBankElement, useElements, useStripe} from '@stripe/react-stripe-js';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {AppContext} from "../../misc/appContext";
import {createSetupIntent} from "../../../util/subscriptionUtil";
import Subscription from "../../../models/subscriptionModel";
import IdealForm from "./idealForm";
import {IdealErrors} from "../../../util/interfaces";

export default function IdealController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const userEmail = user?.email ?? '';
    const userName = user?.fullName ?? '';
    const stripe = useStripe(),
        elements = useElements(),
        [name, setName] = useState(userName),
        [email, setEmail] = useState(userEmail),
        [loading, setLoading] = useState(true),
        [errors, setErrors] = useState<IdealErrors>({name: null, email: null});
    let clientSecret = useRef<string | null>(null);

    function onChangeName(value: string) {
        setName(value);
        onResetError('name')();
    }

    function onChangeEmail(value: string) {
        setEmail(value);
        onResetError('email')();
    }

    async function onSubmit() {
        if (!stripe || !elements || !clientSecret.current) return;
        const bank = elements.getElement(IdealBankElement);
        if (!bank) return;
        if (!validateValues()) return;
        setLoading(true);
        const appUrl = getSetting('appUrl');
        const {error} = await stripe.confirmIdealSetup(clientSecret.current, {
            payment_method: {
                ideal: bank,
                billing_details: {
                    name, email
                }
            },
            return_url: `${appUrl}/ideal-complete`,
        });
        if (error) {
            setLoading(false);
            showDialog('Het instellen van iDeal is niet gelukt.', 'Instellen mislukt');
        }
    }

    function validateValues(): boolean {
        const errs = Object.assign({}, errors);
        let valid: boolean = true;
        if (!name) {
            errs.name = 'Vul de naam rekeninghouder in';
            valid = false;
        }
        if (!email) {
            errs.email = 'Vul het e-mailadres in';
            valid = false;
        } else if (!validateEmail(email)) {
            errs.email = 'Vul een geldig e-mailadres in';
            valid = false;
        }
        if (!valid) setErrors(errs);
        return valid;
    }

    const onResetError = (key: keyof IdealErrors) => () => {
        setErrors(Object.assign({}, errors, {[key]: null}));
    };

    function getSetupIntent() {
        if (!account) return;
        setLoading(true);
        createSetupIntent(account.id).then((result: {
            subscription: Subscription,
            success: boolean,
            data: ResponseData
        }) => {
            setLoading(false);
            if (result.success) clientSecret.current = result.data.client_secret;
        });
    }

    useEffect(() => {
        getSetupIntent();
    }, []);

    return (
        <IdealForm onChangeName={onChangeName} onChangeEmail={onChangeEmail} onSubmit={onSubmit}
                   onResetError={onResetError} name={name} email={email} loading={loading} errors={errors}/>
    )
}